'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import RecentMatchesList from './RecentMatchesList';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import LocationRow from '@/components/LocationRow';
import { userModel } from '@/models/userModel';
import type { MatchWithRating } from '@/components/match/types';
import type { Tables } from '@/types/supabase';

type Club = { id: string; name: string; logo: string | null; status: string };

interface ProfileSidebarProps {
  matchHistory: MatchWithRating[];
  player: Tables<'player'>;
}

export default function ProfileSidebar({ matchHistory, player }: ProfileSidebarProps) {
  const t = useTranslations('Profile');
  const [clubs, setClubs] = useState<Club[]>([]);

  // Fetch affiliations (clubs) for any profile
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const clubRows = await userModel.getAffiliatedClubs(player.id);
        if (!mounted) return;
        setClubs(clubRows as Club[]);
      } catch {
        if (mounted) setClubs([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [player.id]);

  const hasAffiliations = clubs.length > 0;

  return (
    <aside className="space-y-4">
      <RecentMatchesList matches={matchHistory} />

      {hasAffiliations && (
        <Card>
          <CardHeader>
            <CardTitle>{t('affiliations')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {clubs.length > 0 && (
              <div>
                <h4 className="text-muted mb-2 text-xs font-medium tracking-wider uppercase">
                  {t('affiliatedClubs')}
                </h4>
                <ul className="space-y-2">
                  {clubs.map((c) => (
                    <LocationRow
                      key={c.id}
                      id={c.id}
                      name={c.name}
                      logo={c.logo}
                      status={c.status}
                      type="club"
                    />
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </aside>
  );
}
